import { isAutomotiveBusiness, isBarPubUser, isBrickFactoryUser, isPharmacyUser } from './businessType';
import { canAccessSettings, canModerateBranch } from './userLocale';

/** Not used by brick/tile factories — hidden even during trial. */
const BRICK_FACTORY_EXCLUDED_MODULES = new Set(['printing', 'cashbook', 'other_services', 'sms_monitor']);

/** Never shown for bar/pub — not relevant to drinks retail. */
const BAR_PUB_HIDDEN_MODULES = new Set(['brick', 'printing']);

/** Chemist / pharmacy — no print shop or brick factory tools. */
const PHARMACY_HIDDEN_MODULES = new Set(['brick', 'printing']);

/** Automotive spare-parts shops — no factory, print-shop, wakala, or generic other-services menus. */
const AUTOMOTIVE_HIDDEN_MODULES = new Set(['brick', 'printing', 'cashbook', 'sms_monitor', 'other_services']);

/** Bar MVP (no M-Pesa desk) — wakala & bar extras off by default. */
const BAR_PUB_MVP_HIDDEN_MODULES = new Set(['cashbook', 'sms_monitor', 'other_services']);

/** Maps sidebar menu keys to SaaS module_key from backend. */
const MENU_TO_MODULE = {
  users: 'users',
  orders: 'orders',
  stock: 'stock',
  'factory-stock': 'stock',
  'auto-stock': 'stock',
  pos: 'pos',
  printing: 'printing',
  expenses: 'expenses',
  cash: 'cashbook',
  reports: 'reports',
  security: 'security',
  settings: 'security',
  brick: 'brick',
  subscription: 'subscription',
  debts: 'debts',
};

/** Pages reachable when subscription expired (renewal & configuration). */
const SETTINGS_PAGE_KEYS = new Set([
  'settings',
  'settings-notifications',
  'settings-appearance',
  'settings-account',
  'debts-list',
  'business-settings',
  'subscription',
  'subscription-checkout',
  'security-logs',
  'security-password',
  'support',
  'security-tips',
  'branches-list',
  'users-list',
  'users-add',
]);

export function isSettingsPage(pageKey) {
  return SETTINGS_PAGE_KEYS.has(pageKey);
}

/** Page keys that require a specific module (beyond parent menu). */
const PAGE_EXTRA_MODULE = {
  'pos-other-services': 'other_services',
  'cash-sms-monitor': 'sms_monitor',
  'auto-products-record': 'stock',
  'auto-products-opening': 'stock',
  'auto-products-categories': 'stock',
  'auto-products-brands': 'stock',
  'auto-products-units': 'stock',
  'auto-inventory': 'stock',
  'auto-suppliers': 'orders',
};

export function getEnabledModules(user) {
  const m = user?.modules;
  if (!Array.isArray(m)) return null;
  return m;
}

/** Active 7-day (or configured) trial with full product access. */
export function isOnActiveTrial(user) {
  const sub = user?.subscription;
  if (!sub) return false;
  if (sub.allowsAccess === false) return false;
  if (sub.trialFullAccess === true) return true;
  if (sub.status !== 'trial') return false;
  const days = sub.daysRemaining ?? sub.daysLeftInTrial ?? 0;
  return days > 0 || sub.onTrial === true;
}

function brickFactoryExcludesModule(moduleKey) {
  return BRICK_FACTORY_EXCLUDED_MODULES.has(moduleKey);
}

function brickFactoryExcludesPage(pageKey) {
  if (!pageKey) return false;
  if (pageKey === 'printing' || pageKey.startsWith('printing-')) return true;
  if (pageKey === 'cash' || pageKey.startsWith('cash-')) return true;
  if (pageKey === 'report-printing' || pageKey === 'report-wakala') return true;
  if (pageKey === 'pos-other-services') return true;
  return false;
}

function barPubExcludesModule(moduleKey, user) {
  if (!isBarPubUser(user)) return false;
  if (BAR_PUB_HIDDEN_MODULES.has(moduleKey)) return true;
  if (user?.tenant?.businessType === 'bar_pub' && BAR_PUB_MVP_HIDDEN_MODULES.has(moduleKey)) {
    return true;
  }
  return false;
}

function pharmacyExcludesModule(moduleKey, user) {
  return isPharmacyUser(user) && PHARMACY_HIDDEN_MODULES.has(moduleKey);
}

function automotiveExcludesModule(moduleKey, user) {
  return isAutomotiveBusiness(user) && AUTOMOTIVE_HIDDEN_MODULES.has(moduleKey);
}

function pharmacyExcludesPage(pageKey) {
  if (!pageKey) return false;
  if (pageKey === 'brick' || pageKey.startsWith('brick-')) return true;
  if (pageKey === 'raw-materials-overview') return true;
  if (pageKey === 'printing' || pageKey.startsWith('printing-')) return true;
  if (pageKey === 'report-printing') return true;
  if (pageKey === 'brick-deliveries') return true;
  if (pageKey === 'brick-pre-orders') return true;
  if (pageKey === 'brick-customer-docs') return true;
  return false;
}

function automotiveExcludesPage(pageKey) {
  if (!pageKey) return false;
  if (pageKey === 'brick' || pageKey.startsWith('brick-')) return true;
  if (pageKey === 'raw-materials-overview') return true;
  if (pageKey === 'printing' || pageKey.startsWith('printing-')) return true;
  if (pageKey === 'cash' || pageKey.startsWith('cash-')) return true;
  if (pageKey === 'report-printing' || pageKey === 'report-wakala') return true;
  if (pageKey === 'pos-other-services') return true;
  return false;
}

/** Trial unlocks every module except scoped business types that must stay role-specific. */
function trialUnlocksAllModules(user) {
  if (!isOnActiveTrial(user)) return false;
  if (isBrickFactoryUser(user) || isBarPubUser(user) || isPharmacyUser(user) || isAutomotiveBusiness(user)) return false;
  return true;
}

function barPubExcludesPage(pageKey) {
  if (!pageKey) return false;
  if (pageKey === 'brick' || pageKey.startsWith('brick-')) return true;
  if (pageKey === 'raw-materials-overview') return true;
  if (pageKey === 'printing' || pageKey.startsWith('printing-')) return true;
  if (pageKey === 'report-printing') return true;
  if (pageKey === 'brick-deliveries') return true;
  if (pageKey === 'brick-pre-orders') return true;
  if (pageKey === 'brick-customer-docs') return true;
  if (pageKey === 'damaged-products') return true;
  return false;
}

function barPubMvpExcludesPage(pageKey, user) {
  if (user?.tenant?.businessType !== 'bar_pub') return false;
  if (pageKey === 'cash' || pageKey.startsWith('cash-')) return true;
  if (pageKey === 'report-wakala') return true;
  if (pageKey === 'pos-other-services') return true;
  return false;
}

export function hasTenantModule(user, moduleKey) {
  if (isBrickFactoryUser(user) && brickFactoryExcludesModule(moduleKey)) return false;
  if (barPubExcludesModule(moduleKey, user)) return false;
  if (pharmacyExcludesModule(moduleKey, user)) return false;
  if (automotiveExcludesModule(moduleKey, user)) return false;
  if (trialUnlocksAllModules(user)) return true;
  const enabled = getEnabledModules(user);
  if (enabled === null) return true;
  return enabled.includes(moduleKey);
}

export function menuKeyAllowed(menuKey, user) {
  if (menuKey === 'expenses' && !canModerateBranch(user?.role)) return false;
  if (menuKey === 'settings' && !canAccessSettings(user?.role)) return false;
  if (menuKey === 'overview' || menuKey === 'settings') return true;
  if (isBrickFactoryUser(user) && (menuKey === 'printing' || menuKey === 'cash')) return false;
  if (isBarPubUser(user) && (menuKey === 'printing' || menuKey === 'brick')) return false;
  if (isPharmacyUser(user) && (menuKey === 'printing' || menuKey === 'brick')) return false;
  if (isAutomotiveBusiness(user) && (menuKey === 'printing' || menuKey === 'cash' || menuKey === 'brick')) return false;
  if (menuKey === 'auto-manual') return isAutomotiveBusiness(user);
  if (isBarPubUser(user) && user?.tenant?.businessType === 'bar_pub' && menuKey === 'cash') {
    return false;
  }
  if (trialUnlocksAllModules(user)) return true;
  const mod = MENU_TO_MODULE[menuKey];
  if (!mod) return true;
  return hasTenantModule(user, mod);
}

export function tenantMayAccessPage(pageKey, user) {
  if (!pageKey || pageKey === 'overview') return true;
  if (isBrickFactoryUser(user) && brickFactoryExcludesPage(pageKey)) return false;
  if (isBarPubUser(user) && (barPubExcludesPage(pageKey) || barPubMvpExcludesPage(pageKey, user))) {
    return false;
  }
  if (isPharmacyUser(user) && pharmacyExcludesPage(pageKey)) return false;
  if (isAutomotiveBusiness(user) && automotiveExcludesPage(pageKey)) return false;
  if (trialUnlocksAllModules(user)) return true;
  const extra = PAGE_EXTRA_MODULE[pageKey];
  if (extra) return hasTenantModule(user, extra);
  for (const [menuKey, mod] of Object.entries(MENU_TO_MODULE)) {
    if (pageKey === menuKey || pageKey.startsWith(`${menuKey}-`)) {
      return hasTenantModule(user, mod);
    }
  }
  if (pageKey.startsWith('report-')) return hasTenantModule(user, 'reports');
  if (pageKey === 'expenses' || pageKey.startsWith('expenses-')) {
    return hasTenantModule(user, 'expenses') && canModerateBranch(user?.role);
  }
  if (pageKey.startsWith('cash-')) return hasTenantModule(user, 'cashbook');
  if (pageKey.startsWith('printing-')) return hasTenantModule(user, 'printing');
  if (
    pageKey.startsWith('stock-') ||
    pageKey === 'damaged-products' ||
    pageKey === 'add-raw-material' ||
    pageKey === 'add-raw-material-category' ||
    pageKey === 'raw-materials-overview' ||
    pageKey === 'factory-stock-overview'
  ) {
    return hasTenantModule(user, 'stock');
  }
  if (pageKey === 'debts-list') {
    return hasTenantModule(user, 'debts') || hasTenantModule(user, 'pos');
  }
  if (pageKey.startsWith('pos-')) return hasTenantModule(user, 'pos');
  if (pageKey === 'brick-deliveries') {
    return hasTenantModule(user, 'pos') || hasTenantModule(user, 'brick');
  }
  if (pageKey === 'factory-suppliers' || pageKey.startsWith('orders')) {
    return hasTenantModule(user, 'orders');
  }
  if (pageKey === 'brick' || pageKey.startsWith('brick-')) {
    return hasTenantModule(user, 'brick');
  }
  if (pageKey.startsWith('users-')) {
    return hasTenantModule(user, 'users');
  }
  if (pageKey === 'branches-list' || pageKey === 'business-settings') {
    return hasTenantModule(user, 'users');
  }
  if (
    pageKey === 'settings' ||
    pageKey === 'settings-notifications' ||
    pageKey === 'settings-appearance' ||
    pageKey === 'settings-account'
  ) {
    return true;
  }
  if (pageKey === 'subscription' || pageKey === 'subscription-checkout') {
    return hasTenantModule(user, 'subscription') || hasTenantModule(user, 'security');
  }
  if (pageKey.startsWith('security-') || pageKey === 'support') {
    return hasTenantModule(user, 'security');
  }
  return true;
}

export function filterSubmenu(submenu, user) {
  if (!submenu) return submenu;
  let list = submenu;
  if (isBrickFactoryUser(user)) {
    list = list.filter((sub) => !brickFactoryExcludesPage(sub.key));
  }
  if (isBarPubUser(user)) {
    list = list.filter(
      (sub) => !barPubExcludesPage(sub.key) && !barPubMvpExcludesPage(sub.key, user)
    );
  }
  if (isPharmacyUser(user)) {
    list = list.filter((sub) => !pharmacyExcludesPage(sub.key));
  }
  if (trialUnlocksAllModules(user)) {
    return list;
  }
  return list.filter((sub) => {
    if (sub.key === 'pos-other-services') return hasTenantModule(user, 'other_services');
    if (sub.key === 'cash-sms-monitor') return hasTenantModule(user, 'sms_monitor');
    if (sub.key === 'report-wakala') return hasTenantModule(user, 'cashbook');
    if (sub.key === 'report-printing') return hasTenantModule(user, 'printing');
    if (sub.key === 'debts-list') {
      return hasTenantModule(user, 'debts') || hasTenantModule(user, 'pos');
    }
    if (sub.key === 'brick-deliveries') {
      return hasTenantModule(user, 'pos') || hasTenantModule(user, 'brick');
    }
    return true;
  });
}

export function subscriptionAllowsAccess(user) {
  const sub = user?.subscription;
  if (!sub) return true;
  if (sub.allowsAccess === false) return false;
  return true;
}
